// Owned/licensed team badge — replaces the ESPN-CDN team-logo <img>.
//
// Team-sport mode (NBA/NHL/MLB/NFL): colored chip with the team's abbreviation.
//   - Fill color: prefer `color` prop, fall back to `alternateColor` when the
//     primary is near-black/missing, then to a neutral default. This is the
//     same safety net for any sport whose color we don't have (e.g. NFL when
//     ESPN's endpoint is down).
//   - Label color: black or white based on the chip background's luminance —
//     never fixed.
//
// UFC mode (sport='UFC'): neutral chip with the fighter's shortName label
// ("A. Carnelossi"). No color — fighters don't have team colors. Label color
// is the neutral text color used elsewhere.
//
// API kept backward-compatible with the previous TeamLogoImg signature so the
// 4 existing call sites (App.jsx, BracketMode x3) don't need touching beyond
// adding optional color/altColor props where the live game payload provides
// them. `directUrl`, `borderColor`, `background`, `className`, `style` props
// preserved as no-ops or passthroughs for the same reason.

import { NEUTRAL_BG, NEUTRAL_TEXT, pickFill, pickLabel, fighterInitial } from '../lib/teamColors'

// Derive a short label from the team name when no abbreviation is provided.
// Last word, sliced to 3 chars, uppercase.
function deriveLabel(team) {
  return ((team || '').split(' ').pop() || '???').slice(0, 3).toUpperCase()
}

export default function TeamLogoImg({
  team,
  abbr,
  sport,
  color,
  alternateColor,
  shortName,           // UFC fighters use this for the badge label
  size = 32,
  borderColor = 'transparent',
  className,
  style = {},
  // Preserved for prop-compat with old call sites; intentionally unused.
  // eslint-disable-next-line no-unused-vars
  background,
  // eslint-disable-next-line no-unused-vars
  directUrl,
}) {
  const isUFC = sport === 'UFC'

  let bg
  let labelColor
  let label
  let fontSize

  if (isUFC) {
    // UFC fighter — no color. Neutral chip with a single uppercase initial
    // from the fighter's last name (shared fighterInitial helper). Shorter
    // label means the font size can match the team-sport abbr chips at the
    // same scale instead of the smaller fit-the-shortName ratio used before.
    bg         = NEUTRAL_BG
    labelColor = NEUTRAL_TEXT
    label      = fighterInitial(shortName || team)
    fontSize   = Math.max(10, Math.round(size * 0.36))
  } else {
    // Team sport — colored chip with abbreviation.
    bg         = pickFill(color, alternateColor)
    labelColor = pickLabel(bg)
    label      = (abbr && abbr.trim()) || deriveLabel(team)
    fontSize   = Math.max(10, Math.round(size * 0.36))
  }

  const ringStyle = borderColor && borderColor !== 'transparent'
    ? { border: `2px solid ${borderColor}` }
    : {}

  return (
    <div
      className={className || 'team-logo'}
      title={team || ''}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg,
        color: labelColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial,sans-serif',
        fontWeight: 700,
        fontSize,
        letterSpacing: isUFC ? 0 : 0.5,
        flexShrink: 0,
        ...ringStyle,
        ...style,
      }}
    >
      {label}
    </div>
  )
}
